import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useRegisterViewRefresh } from "../lib/viewRefresh";

type ComplianceDeficiencySortRow = {
  status: string;
  apparatusId: string;
  apparatusName: string;
  itemName: string;
};

export function compareComplianceDeficiencyRows(
  a: ComplianceDeficiencySortRow,
  b: ComplianceDeficiencySortRow,
  apparatusOrder: ReadonlyMap<string, number>
) {
  const severityRank = (value: string) => (value === "red" ? 0 : value === "yellow" ? 1 : 2);
  const severity = severityRank(a.status) - severityRank(b.status);
  if (severity !== 0) return severity;

  const apparatus =
    (apparatusOrder.get(a.apparatusId) ?? Number.MAX_SAFE_INTEGER) -
    (apparatusOrder.get(b.apparatusId) ?? Number.MAX_SAFE_INTEGER);
  if (apparatus !== 0) return apparatus;
  if (a.apparatusName !== b.apparatusName) return a.apparatusName.localeCompare(b.apparatusName);
  return a.itemName.localeCompare(b.itemName);
}

function apparatusReasonTags(row: any) {
  const tags: string[] = [];
  const gap = Math.max(0, Number(row.requiredQty ?? 0) - Number(row.actualQty ?? 0));

  if ((row.requiredQty ?? 0) > 0 && (row.actualQty ?? 0) <= 0) {
    tags.push(`Missing (${gap})`);
  } else if (gap > 0) {
    tags.push(`Below required by ${gap}`);
  }

  if ((row.expiredQty ?? 0) > 0) {
    tags.push(`Expired ${row.expiredQty}`);
  }

  if ((row.expiring90Qty ?? 0) > 0) {
    tags.push(`Expiring <=90d ${row.expiring90Qty}`);
  }

  if (tags.length === 0 && row.status !== "ok") {
    tags.push("Deficient");
  }

  return tags;
}

export function ComplianceScreen() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [apparatusFilter, setApparatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = async (forceRefresh = false) => {
    setLoading(true);
    setError("");
    try {
      const result = await api.getComplianceDeficiencies(
        undefined,
        forceRefresh ? { forceRefresh: true } : undefined
      );
      setData(result);
    } catch (err: any) {
      setError(err?.message ?? "Failed to load compliance");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useRegisterViewRefresh(() => refresh(true), {
    disabled: loading,
    disabledReason: "Compliance is already loading."
  });

  const scopedApparatus = useMemo(() => {
    const rows = data?.apparatus ?? [];
    if (apparatusFilter === "all") return rows;
    return rows.filter((row: any) => row.apparatusId === apparatusFilter);
  }, [data, apparatusFilter]);

  const deficiencyRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const apparatusOrder = new Map<string, number>(
      scopedApparatus.map((app: any, index: number) => [String(app.apparatusId), index])
    );

    return scopedApparatus
      .flatMap((app: any) =>
        (app.items ?? [])
          .filter((row: any) => row.status !== "ok")
          .map((row: any) => ({
            ...row,
            reasons: apparatusReasonTags(row)
          }))
      )
      .filter((row: any) => {
        if (!q) return true;
        const text = `${row.apparatusName} ${row.itemName} ${row.category ?? ""} ${row.subcategory ?? ""} ${row.reasons.join(" ")}`.toLowerCase();
        return text.includes(q);
      })
      .sort((a: any, b: any) => compareComplianceDeficiencyRows(a, b, apparatusOrder));
  }, [scopedApparatus, search]);

  const redRows = useMemo(
    () => deficiencyRows.filter((row: any) => row.status === "red" && row.countsForCompliance !== false),
    [deficiencyRows]
  );
  const yellowRows = useMemo(
    () => deficiencyRows.filter((row: any) => row.status === "yellow" || row.alertOnly || row.countsForCompliance === false),
    [deficiencyRows]
  );
  const selectedApparatusName =
    apparatusFilter === "all" ? "All QRS apparatus" : scopedApparatus[0]?.apparatusName ?? "Selected apparatus";
  const showApparatusColumn = apparatusFilter === "all";
  const noncompliantApparatusCount = scopedApparatus.filter((row: any) => row.isNoncompliant).length;

  if (error && !data) return <p className="error-text">{error}</p>;
  if (!data) return <p className="muted">Loading compliance...</p>;

  return (
    <div className="stack-md">
      {error ? <p className="error-text">{error}</p> : null}
      <section className="card stack-sm">
        <div className="row-between">
          <h2>QRS Compliance</h2>
          <div className="compliance-toolbar">
            <select className="input compliance-select" value={apparatusFilter} onChange={(event) => setApparatusFilter(event.target.value)}>
              <option value="all">All Apparatus</option>
              {(data.apparatus ?? []).map((row: any) => (
                <option key={row.apparatusId} value={row.apparatusId}>
                  {row.apparatusName}
                </option>
              ))}
            </select>
            <input
              className="input compliance-search"
              placeholder="Search deficiencies"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
        <p className="muted small">Line-by-line compliance details based on required on apparatus vs currently on apparatus.</p>
        <p className="muted small" role="status">
          {loading
            ? "Updating…"
            : data.generatedAt
              ? `Last updated ${new Date(data.generatedAt).toLocaleString()}`
              : null}
        </p>
        <div className="compliance-metrics">
          <span>
            Scope: <strong>{selectedApparatusName}</strong>
          </span>
          <span>
            Noncompliant apparatus: <strong className="red">{noncompliantApparatusCount}</strong>
          </span>
          <span>
            Deficiencies: <strong className="red">{redRows.length}</strong>
          </span>
          <span>
            Alerts: <strong className="yellow">{yellowRows.length}</strong>
          </span>
        </div>
      </section>

      <section className="card stack-sm">
        <h3>Non-compliant</h3>
        <div className="compliance-table-wrap">
          <table className="compliance-table">
            <thead>
              <tr>
                {showApparatusColumn ? <th>Apparatus</th> : null}
                <th>Item</th>
                <th>Required</th>
                <th>On Apparatus</th>
                <th>Expired</th>
                <th>Expiring &lt;=90d</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {redRows.map((row: any) => (
                <tr key={`${row.apparatusId}-${row.catalogItemId}`} className="compliance-row-red">
                  {showApparatusColumn ? <td>{row.apparatusName}</td> : null}
                  <td className="compliance-item-cell">
                    <strong className="text-wrap-anywhere">{row.itemName}</strong>
                    {row.category ? <div className="muted small">{row.category}</div> : null}
                    {row.bagNames ? <div className="muted small">{row.bagNames}</div> : null}
                  </td>
                  <td>{row.requiredQty}</td>
                  <td>{row.actualQty}</td>
                  <td>{row.expiredQty}</td>
                  <td>{row.expiring90Qty}</td>
                  <td>
                    <div className="reason-tags">
                      {row.reasons.map((reason: string) => (
                        <span key={`${row.catalogItemId}-${reason}`} className="reason-chip reason-chip-red">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
              {redRows.length === 0 ? (
                <tr>
                  <td colSpan={showApparatusColumn ? 7 : 6} className="muted small">
                    No red deficiencies in this scope.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card stack-sm">
        <h3>Alerts</h3>
        <div className="compliance-table-wrap">
          <table className="compliance-table">
            <thead>
              <tr>
                {showApparatusColumn ? <th>Apparatus</th> : null}
                <th>Item</th>
                <th>Required</th>
                <th>On Apparatus</th>
                <th>Expired</th>
                <th>Expiring &lt;=90d</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {yellowRows.map((row: any) => (
                <tr key={`${row.apparatusId}-${row.catalogItemId}`} className="compliance-row-yellow">
                  {showApparatusColumn ? <td>{row.apparatusName}</td> : null}
                  <td className="compliance-item-cell">
                    <strong className="text-wrap-anywhere">{row.itemName}</strong>
                    {row.category ? <div className="muted small">{row.category}</div> : null}
                    {row.bagNames ? <div className="muted small">{row.bagNames}</div> : null}
                  </td>
                  <td>{row.requiredQty}</td>
                  <td>{row.actualQty}</td>
                  <td>{row.expiredQty}</td>
                  <td>{row.expiring90Qty}</td>
                  <td>
                    <div className="reason-tags">
                      {row.reasons.map((reason: string) => (
                        <span key={`${row.catalogItemId}-${reason}`} className="reason-chip reason-chip-yellow">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
              {yellowRows.length === 0 ? (
                <tr>
                  <td colSpan={showApparatusColumn ? 7 : 6} className="muted small">
                    No yellow alerts in this scope.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
